import { PricingI } from "../../../../../../api/request/Lead/Interfaces/PricingInterface";

/**
 * Prima del área de cliente (`GET /api/dashboard/pricing/{leadId}`).
 *
 * Desde gf-monorepo #10298, en la cohorte SCOR FR `price` y `promotedPrice` llegan
 * a `null` (y `showPromo` a `false`) cuando no hay una prima que ms-scor afirme.
 * Sin prima no se pinta nunca un importe (ni «0,00 €» ni uno calculado con 0):
 * se pinta un texto neutro. Con un `price` numérico, todo como siempre.
 *
 * Clave pendiente de crear en `getlife/translations` (ver docs/i18n-pending.md);
 * mientras no llegue, el literal del idioma.
 */
export const PRICE_UPDATING_KEY = "newDashboard.price.updating";

type FallbackLocale = "es" | "fr" | "it" | "en" | "pt";

export const PRICE_UPDATING_FALLBACKS: Record<FallbackLocale, string> = {
  es: "Importe en actualización",
  fr: "Montant en cours de mise à jour",
  it: "Importo in aggiornamento",
  en: "Amount being updated",
  pt: "Montante em atualização",
};

export type TranslateFn = (key: string) => string;

const fallbackLocale = (locale: string | undefined): FallbackLocale => {
  if ("fr" === locale || "it" === locale || "en" === locale || "pt" === locale) return locale;
  // es, wl_es, sql_es y cualquier otro.
  return "es";
};

export const priceUpdatingText = (
  translate: TranslateFn,
  locale: string | undefined = process.env.REACT_APP_DOMAIN_LOCALE
): string => {
  const translated = translate(PRICE_UPDATING_KEY);
  if (!translated || PRICE_UPDATING_KEY === translated) {
    return PRICE_UPDATING_FALLBACKS[fallbackLocale(locale)];
  }
  return translated;
};

/**
 * ¿Hay un importe que se pueda pintar? `null`/`undefined` (o no numérico) = no. Un string
 * numérico («11.11») cuenta como importe: el código de antes lo pintaba igual y no se ha
 * podido comprobar qué tipo manda el gateway native.
 */
export const isKnownPrice = (value: unknown): boolean =>
  ("number" === typeof value && Number.isFinite(value)) ||
  ("string" === typeof value && "" !== value.trim() && Number.isFinite(Number(value)));

const asNumber = (value: unknown): number => ("number" === typeof value ? value : Number(value));

export interface DashboardPriceI {
  /** `null` = sin importe: se pinta `priceUpdatingText`. */
  regularPrice: number | null;
  /** `null` = sin importe: se pinta `priceUpdatingText`. */
  displayPrice: number | null;
  hasPromo: boolean;
}

/**
 * Precio de las tarjetas «Cuota mensual/anual». Con `price` numérico es exactamente
 * el cálculo de siempre (promoción si `promotedPrice > 0` y distinto de `price`).
 */
export const dashboardPrice = (pricing?: PricingI | null): DashboardPriceI => {
  if (!pricing || !isKnownPrice(pricing.price)) {
    return { regularPrice: null, displayPrice: null, hasPromo: false };
  }
  const regularPrice = asNumber(pricing.price);
  const promotedPrice = isKnownPrice(pricing.promotedPrice) ? asNumber(pricing.promotedPrice) : 0;
  const hasPromo = promotedPrice > 0 && promotedPrice !== regularPrice;
  return { regularPrice, displayPrice: hasPromo ? promotedPrice : regularPrice, hasPromo };
};

/**
 * Prima actual para cálculos (subir capital, retención). `null` = sin importe:
 * no se calcula con 0. Con `price` numérico, `promotedPrice || price || 0` como siempre.
 */
export const currentPremiumOf = (pricing?: PricingI | null): number | null => {
  if (!pricing || !isKnownPrice(pricing.price)) return null;
  const promoted = isKnownPrice(pricing.promotedPrice) ? asNumber(pricing.promotedPrice) : 0;
  return promoted || asNumber(pricing.price) || 0;
};
